import { useNavigate } from 'react-router-dom';
import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  isMinimalFiling,
  statusKeys
} from '../../../data';

import UploadIcon from '../../../../../../assets/icons/upload_files.svg?react';
import { FaArrowRight } from 'react-icons/fa6';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { BsPlus } from 'react-icons/bs';
import { useMemo, useRef, useState } from 'react';
import { serviceFundMapping, useUploadFundFiles } from '../../../../../../services/funds.service';
import toast from 'react-hot-toast';
import { LoadingCircle } from '../../../../Funds/icons';
import { HiExternalLink } from 'react-icons/hi';
import { Box, Button, Flex, Modal, Text, Tooltip } from '@mantine/core';
import { TbReplace, TbTrash } from 'react-icons/tb';
import { extractApiError } from '../../../../../../utils';
import {
  useDeletePeriodFile,
  useUpdateUploadedFile
} from '../../../../../../services/period.service';
import { notifications } from '@mantine/notifications';
import AddFilesSourceModal from '../../../../components/AddFilesSourceModal';

export const FilesColumn = ({
  period,
  reportType,
  workflowId,
  refetch,
  setAllowFilesToGetEdited,
  setSelectedPeriodId,
  setIsOpenReadonlyFilesDrawer,
  canManageWorkflow,
  isCloudIntegrationEnabled = false,
  onImportFromIntegration
}) => {
  const navigate = useNavigate();

  const inputRef = useRef();
  const replaceInputRef = useRef();
  const uploadFundFiles = useUploadFundFiles();
  const updateUploadedFile = useUpdateUploadedFile();
  const deleteUploadedFile = useDeletePeriodFile();

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadAction, setUploadAction] = useState(null);

  const status = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getStatusForCustomReportType(period);

    return getStatus(period);
  }, [reportType, period]);

  const placeholder = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period, reportType);
    else if (reportType === REPORT_TYPES.CUSTOM)
      return getPlaceholderForCustomReportType(period, reportType);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);

  const handleClick = () => {
    navigate(`/project/quaters/${workflowId}/${period.id}/upload-files`);
  };

  const handleUploadFiles = () => {
    if (!canManageWorkflow) return;

    setUploadAction('add');
  };

  const handleReplaceSourceFile = () => {
    if (!canManageWorkflow) return;

    setUploadAction('replace');
  };

  const handleCloseUploadModal = () => {
    setUploadAction(null);
  };

  const onUploadFilesChange = e => {
    if (!canManageWorkflow) return;

    const files = e.target.files;
    const totalFiles = files.length;

    if (!totalFiles) return;

    const formData = new FormData();
    [...files].forEach(file => formData.append('files', file));

    let uploadedCount = 0;

    // Create a toast with an ID so it can be updated dynamically
    const toastId = toast.loading(`Uploading ${uploadedCount}/${totalFiles}`, {
      style: {
        width: 200
      },
      icon: <LoadingCircle />,
      position: 'bottom-center'
    });

    uploadFundFiles.mutate(
      {
        data: formData,
        periodId: period?.id,
        onUploadProgress: data => {
          let cumulativeSize = 0;

          // Calculate uploaded file count based on cumulative size
          [...files].forEach((file, idx) => {
            cumulativeSize += file.size;
            if (data.loaded >= cumulativeSize) {
              uploadedCount = idx + 1; // Update uploaded count
            }
          });

          // Update toast with current progress
          toast.loading(`Uploading ${Math.min(uploadedCount, totalFiles)}/${totalFiles}`, {
            id: toastId
          });
        }
      },
      {
        onSuccess: () => {
          // Reset state and trigger a refetch
          refetch();

          // Replace the loading toast with a success message
          toast.dismiss(toastId);
          toast.success('All files uploaded successfully!', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          // Replace the loading toast with an error message
          toast.dismiss(toastId);
          toast.error(extractApiError(err, 'Failed to upload files.'), {
            position: 'bottom-center'
          });
        },
        onSettled: () => {
          // Clear the input value to allow re-upload
          e.target.value = '';
        }
      }
    );
  };

  const onDeleteFile = () => {
    if (!canManageWorkflow) return;

    deleteUploadedFile.mutate(
      {
        periodId: period?.id,
        fileId: selectedFile.id
      },
      {
        onSuccess: () => {
          refetch();
          toast.success('File deleted successfully');
          setSelectedFile(null);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to delete this file.'));
          setSelectedFile(null);
        }
      }
    );
  };

  const onUpdateFile = e => {
    if (!canManageWorkflow) return;

    const { files } = e.target;

    const formData = new FormData();

    formData.append('file', files[0]);

    // Create a toast with an ID so it can be updated dynamically
    const toastId = toast.loading(`Replacing file...`, {
      style: {
        width: 200
      },
      icon: <LoadingCircle />,
      position: 'bottom-center'
    });

    updateUploadedFile.mutate(
      {
        periodId: period?.id,
        data: formData,
        fileId: period?.files?.[0]?.id,
        onUploadProgress: () => {
          // setProgress(Math.round((100 * data.loaded) / data.total));
        }
      },
      {
        onSuccess: () => {
          refetch();
          // Replace the loading toast with a success message
          toast.dismiss(toastId);
          toast.success('File replaced successfully');
        },
        onError: err => {
          toast.dismiss(toastId);
          toast.error(extractApiError(err, 'Failed to replace the file.'));
        },
        onSettled: () => {
          e.target.value = '';
        }
      }
    );
  };

  const handlePostClick = () => {
    if (period.file_count === 0) {
      handleClick();
      return;
    }

    if (status !== statusKeys.FILES && status !== statusKeys.EXTRACTION) {
      setAllowFilesToGetEdited(false);
    } else {
      setAllowFilesToGetEdited(true);
    }

    setSelectedPeriodId(period.id);
    setIsOpenReadonlyFilesDrawer(true);
  };

  const onDownload = async () => {
    try {
      notifications.show({
        loading: true,
        title: 'Downloading',
        position: 'bottom-center',
        autoClose: false
      });

      const response = await serviceFundMapping.downloadFile({
        periodId: period?.id,
        fileId: period?.files?.[0]?.id
      });

      const blob = new Blob([response]);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      link.download = period?.files?.[0]?.name || 'file.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.log(e);
    } finally {
      notifications.clean();
    }
  };

  const renderInputs = () => (
    <>
      <input
        type="file"
        ref={inputRef}
        onChange={onUploadFilesChange}
        multiple={false}
        accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv, .xlsx, .xls, .xlsm"
        style={{ display: 'none' }}
      />

      <input
        type="file"
        ref={replaceInputRef}
        multiple={false}
        onChange={onUpdateFile}
        accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv, .xlsx, .xls, .xlsm"
        style={{ display: 'none' }}
      />

      <Modal
        opened={!!selectedFile}
        radius="sm"
        withCloseButton
        withinPortal
        onClose={() => setSelectedFile(null)}
        centered
        title={`Delete ${selectedFile?.name} File?`}
        size="sm"
      >
        <Box>
          <Text c="gray.7" fz="sm" mb="24">
            Are you sure want to delete File?
          </Text>
          <Flex align="center" gap="16" justify="flex-end">
            <Button onClick={() => setSelectedFile(null)} variant="default">
              Cancel
            </Button>
            <Button
              color="red.8"
              loading={deleteUploadedFile.isLoading}
              disabled={deleteUploadedFile.isLoading}
              onClick={onDeleteFile}
            >
              Yes, Delete
            </Button>
          </Flex>
        </Box>
      </Modal>

      <AddFilesSourceModal
        opened={!!uploadAction}
        onClose={handleCloseUploadModal}
        onUploadFromDevice={() => {
          const action = uploadAction;
          handleCloseUploadModal();
          if (action === 'replace') {
            replaceInputRef.current?.click();
          } else {
            inputRef.current?.click();
          }
        }}
        onImportFromSource={() => {
          handleCloseUploadModal();
          onImportFromIntegration?.(period?.id);
        }}
        title={uploadAction === 'replace' ? 'Replace Source File' : 'Add Source File'}
        description={
          uploadAction === 'replace'
            ? 'Choose how you want to replace this source file.'
            : 'Choose how you want to add this source file.'
        }
        isCloudIntegrationEnabled={isCloudIntegrationEnabled}
      />
    </>
  );

  if (isMinimalFiling(period)) {
    return <span style={{ color: '#adb5bd', fontSize: 14 }}>N/A</span>;
  }

  if (reportType === REPORT_TYPES.CONVERSION) {
    if (status === statusKeys.FILES && !period.file_count)
      return (
        <>
          <ActionLabel
            label="Add Source File"
            rightIcon={<BsPlus size={20} />}
            onClick={canManageWorkflow ? handleUploadFiles : undefined}
          />

          {renderInputs()}
        </>
      );

    return (
      <>
        <Tooltip label={`${period?.files?.[0]?.name || ''}`}>
          <button style={{ width: '100%' }}>
            <ActionLabel
              label="File Uploaded"
              color="#40C057"
              rightIcon={<HiExternalLink size={16} />}
              onClick={onDownload}
              rightSection={
                canManageWorkflow ? (
                  <Flex gap={8} justify="flex-end">
                    <Tooltip label="Replace Source File" position="bottom">
                      <Button
                        size="xs"
                        p={0}
                        variant="transparent"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleReplaceSourceFile();
                        }}
                      >
                        <TbReplace size={16} />
                      </Button>
                    </Tooltip>

                    <Tooltip label="Delete Source File" position="bottom">
                      <Button
                        size="xs"
                        p={0}
                        variant="transparent"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFile(period?.files?.[0]);
                        }}
                      >
                        <TbTrash size={16} color="#ADB5BD" />
                      </Button>
                    </Tooltip>
                  </Flex>
                ) : null
              }
            />
          </button>
        </Tooltip>

        {renderInputs()}
      </>
    );
  }

  if (status === statusKeys.FILES && !period.file_count)
    return <ActionLabel label="Upload files" leftIcon={<UploadIcon />} onClick={handleClick} />;

  if (!period.file_count && (status === statusKeys.AIF || status === statusKeys.JURISDICTION))
    return <ActionLabel placeholder={placeholder} />;

  return (
    <ActionLabel
      label={`${period.file_count} files`}
      rightIcon={<FaArrowRight />}
      onClick={handlePostClick}
    />
  );
};
